/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
} from 'react';
import { View, useWindowDimensions } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { useTranslation } from 'react-i18next';
import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withDelay,
  withTiming,
} from 'react-native-reanimated';
import { LoadingStateView } from '../../components/LoadingStateView';
import { EmptyStateView } from '../../components/EmptyStateView';
import { FeedListWidget } from '../../widgets/FeedListWidget';
import { FooterView } from '../../components/FooterView';
import { SocialFeedAnimationView } from '../../components/SocialFeedAnimationView';
import { useSocialFeedLegacy } from '../../hooks/useSocialFeed';
import { useSocialFeedPageContext } from '../../context/SocialFeedPageContext';
import { useSocialAnalytics } from '../../analytics';
import type {
  Post,
  SocialFeedData,
  SocialFeedPageProps,
  SocialFeedState,
} from './types';
import type { MovementsHomeResponse } from '../../services/types';
import { FeedSectionView } from '../../components/FeedSectionView';
import { URLS } from '../../constants/urls';
import { useAppConfig } from '@airasia-rn/ui';

type ApiErrorShape = {
  response?: { status?: number; data?: { message?: string } };
  request?: unknown;
  message?: string;
};

const parseApiError = (
  error: unknown,
): { code: string; message: string; description: string } => {
  const err = error as ApiErrorShape;
  if (err?.response) {
    const code = err.response.status?.toString() ?? 'API_ERROR';
    const message = err.message ?? 'API request failed';
    return {
      code,
      message,
      description: err.response.data?.message ?? message,
    };
  }
  if (err?.request) {
    const message = err.message ?? 'Network error';
    return { code: 'NETWORK_ERROR', message, description: message };
  }
  if (error instanceof Error) {
    return {
      code: 'API_ERROR',
      message: error.message,
      description: error.message,
    };
  }
  return {
    code: 'API_ERROR',
    message: 'Unknown error',
    description: 'Unknown error',
  };
};

// Offset from top of the screen for the animation overlay (accounts for header/tab height)
const ANIMATION_OVERLAY_TOP_OFFSET = 90;
// Feed reveal starts after 500ms delay once Phase 2 begins, slides up and fades in
const FEED_REVEAL_DELAY_MS = 500;
const FEED_REVEAL_DURATION_MS = 1000;
const BOTTOM_EASING = Easing.out(Easing.cubic);
const FEED_REVEAL_TIMING = {
  duration: FEED_REVEAL_DURATION_MS,
  easing: BOTTOM_EASING,
};

type SocialFeedPageViewProps = SocialFeedPageProps & {
  state: SocialFeedState;
  data?: MovementsHomeResponse | null;
  runFeedAnimation?: boolean;
  animationDone?: boolean;
  onAnimationEnd?: () => void;
};

const handleRetry = (onRetry?: () => void) => () => {
  onRetry?.();
};

const SocialFeedPageView = ({
  state,
  data,
  runFeedAnimation = false,
  animationDone = false,
  onAnimationEnd,
  onSectionHeaderClick,
  onFeedItemClick,
  onFooterChipClick,
  onRetry,
  onEmptyState,
  onNoInternetState,
  onResultState,
  onLoadingState,
}: SocialFeedPageViewProps) => {
  const { t } = useTranslation('social');
  const { height: screenHeight } = useWindowDimensions();

  const emptyData: MovementsHomeResponse = {
    request_id: '',
    locale: '',
    sections: [],
    chips: [],
  };

  const feedData = data || emptyData;
  const sections = feedData.sections || [];
  const chips = feedData.chips || [];
  const hasSections = sections.length > 0;
  const hasChips = chips.length > 0;

  const showAnimation =
    runFeedAnimation && !animationDone && (hasSections || hasChips);

  const animationImages = useMemo(() => {
    if (!showAnimation) return undefined;
    const firstSectionPosts = feedData.sections[0]?.posts ?? [];
    return [
      firstSectionPosts[0]?.media?.thumbnail?.url || undefined,
      firstSectionPosts[1]?.media?.thumbnail?.url || undefined,
      firstSectionPosts[2]?.media?.thumbnail?.url || undefined,
    ] as [string?, string?, string?];
  }, [showAnimation, feedData.sections[0]]);

  // Feed container — starts off-screen below, slides up while invisible, then appears
  const bottomTranslateY = useSharedValue(screenHeight);
  const bottomOpacity = useSharedValue(0);

  const handlePhaseTwo = useCallback(() => {
    // Feed slides up from bottom after delay (synced with Phase 2)
    bottomTranslateY.value = withDelay(
      FEED_REVEAL_DELAY_MS,
      withTiming(0, FEED_REVEAL_TIMING),
    );
    // Instantly show the feed once it has fully moved into position
    bottomOpacity.value = withDelay(
      FEED_REVEAL_DELAY_MS + FEED_REVEAL_DURATION_MS,
      withTiming(1, { duration: 0 }),
    );
  }, [bottomTranslateY, bottomOpacity]);

  const bottomAnimatedStyle = useAnimatedStyle(
    () => ({
      opacity: bottomOpacity.value,
      transform: [{ translateY: bottomTranslateY.value }],
    }),
    [bottomOpacity, bottomTranslateY],
  );

  useEffect(() => {
    if (state === 'loading') {
      onLoadingState?.();
    }
  }, [state, onLoadingState]);

  useEffect(() => {
    if (state === 'noInternet') {
      onNoInternetState?.();
    }
  }, [state, onNoInternetState]);

  useEffect(() => {
    if (state === 'result' && !hasSections && !hasChips) {
      onEmptyState?.();
    }
  }, [state, hasSections, hasChips, onEmptyState]);

  useEffect(() => {
    if (state === 'result' && (hasSections || hasChips)) {
      onResultState?.(feedData);
    }
  }, [state, hasSections, hasChips, onResultState, feedData]);

  const renderLoadingState = () => <LoadingStateView />;

  const renderErrorState = () => (
    <EmptyStateView
      imageUrl={URLS.API_ERROR}
      title={t('movement.errorTitle')}
      description={t('movement.errorDescription')}
      cta={t('movement.buttonRetry')}
      ctaClicked={handleRetry(onRetry)}
    />
  );

  const renderEmptyState = () => (
    <EmptyStateView
      imageUrl={URLS.NO_RESULT_FOUND}
      title={t('movement.emptyResult')}
    />
  );

  const renderNoInternetState = () => (
    <EmptyStateView
      image={require('../../assets/images/img_no_internet.png')}
      title={t('movement.noInternetTitle')}
      description={t('movement.noInternetDescription')}
      cta={t('movement.buttonRetry')}
      ctaClicked={handleRetry(onRetry)}
    />
  );

  const renderResultState = () => {
    if (!hasSections && !hasChips) {
      return renderEmptyState();
    }

    return (
      <View style={styles.animationWrapper}>
        <Animated.View
          style={[
            styles.container,
            showAnimation ? bottomAnimatedStyle : undefined,
          ]}
        >
          {feedData.sections?.map((section) => (
            <FeedSectionView
              key={section.section_id}
              section={section}
              onSectionHeaderClick={onSectionHeaderClick}
              onFeedItemClick={onFeedItemClick}
            />
          ))}
          {hasChips && (
            <FooterView
              chips={feedData.chips}
              onChipClick={onFooterChipClick}
            />
          )}
        </Animated.View>

        {showAnimation && animationImages && (
          <Animated.View style={styles.animationOverlay}>
            <SocialFeedAnimationView
              onAnimationEnd={onAnimationEnd}
              onPhaseTwo={handlePhaseTwo}
              images={animationImages}
            />
          </Animated.View>
        )}
      </View>
    );
  };

  return (
    <View style={styles.container}>
      {state === 'loading' && renderLoadingState()}
      {state === 'error' && renderErrorState()}
      {state === 'empty' && renderEmptyState()}
      {state === 'noInternet' && renderNoInternetState()}
      {state === 'result' && renderResultState()}
    </View>
  );
};

export const SocialFeedPage = ({
  runFeedAnimation,
  onSectionHeaderClick,
  onFeedItemClick,
  onFooterChipClick,
  onRetry,
  onEmptyState,
  onNoInternetState,
  onResultState,
  onLoadingState,
  onAnimationStart,
  onAnimationEnd,
}: SocialFeedPageProps) => {
  const { recommendationHashKey } = useSocialFeedPageContext();
  const { config } = useAppConfig();
  const { trackFeedItemClick, trackApiError } = useSocialAnalytics();

  const handleFeedItemClick = useCallback(
    (post: Post, index: number) => {
      trackFeedItemClick({ post, currency: config.currency ?? '', index });
      onFeedItemClick?.(post, index);
    },
    [onFeedItemClick, config.currency, trackFeedItemClick],
  );
  const movementsHomeQuery = useSocialFeedLegacy();
  const lastTrackedErrorRef = useRef<{ label: string } | null>(null);

  useEffect(() => {
    if (!movementsHomeQuery.isError || !movementsHomeQuery.error) return;
    const { code, message, description } = parseApiError(
      movementsHomeQuery.error,
    );
    const label = `${code}|${message}|${config.locale ?? ''}|${config.geoId ?? ''}`;
    if (lastTrackedErrorRef.current?.label === label) return;
    lastTrackedErrorRef.current = { label };
    trackApiError({
      code,
      category: 'feed',
      description,
      functionality: 'fetch movements home',
      label,
    });
  }, [
    movementsHomeQuery.isError,
    movementsHomeQuery.error,
    config.locale,
    config.geoId,
    trackApiError,
  ]);

  const [animationDone, setAnimationDone] = useState(false);

  useEffect(() => {
    if (!runFeedAnimation) return;
    if (!movementsHomeQuery.data) return;

    const firstPage = movementsHomeQuery.data.pages?.[0];
    const hasSections = (firstPage?.sections || []).length > 0;
    const hasChips = (firstPage?.chips || []).length > 0;
    if (!hasSections && !hasChips) return;
    if (animationDone) return;

    onAnimationStart?.();
  }, [
    animationDone,
    movementsHomeQuery.data,
    runFeedAnimation,
    onAnimationStart,
  ]);

  const handleAnimationEnd = useCallback(() => {
    setAnimationDone(true);
    onAnimationEnd?.();
  }, [onAnimationEnd]);

  const derivedState: SocialFeedState = (() => {
    if (
      movementsHomeQuery.isPending ||
      movementsHomeQuery.isFetching ||
      movementsHomeQuery.isLoading
    )
      return 'loading';
    if (movementsHomeQuery.isError) {
      const { code } = parseApiError(movementsHomeQuery.error);
      return code === 'NETWORK_ERROR' ? 'noInternet' : 'error';
    }
    return 'result';
  })();

  const handleRetry = async () => {
    await movementsHomeQuery.refetch();
    onRetry?.();
  };

  return (
    <SocialFeedPageView
      recommendationHashKey={recommendationHashKey}
      runFeedAnimation={runFeedAnimation}
      animationDone={animationDone}
      onAnimationEnd={handleAnimationEnd}
      state={derivedState}
      data={movementsHomeQuery.data?.pages?.[0]}
      onSectionHeaderClick={onSectionHeaderClick}
      onFeedItemClick={handleFeedItemClick}
      onFooterChipClick={onFooterChipClick}
      onRetry={handleRetry}
      onEmptyState={onEmptyState}
      onNoInternetState={onNoInternetState}
      onResultState={onResultState}
      onLoadingState={onLoadingState}
    />
  );
};

const styles = StyleSheet.create(() => ({
  container: {
    flex: 1,
    paddingBottom: 16,
  },
  animationWrapper: {
    flex: 1,
    position: 'relative' as const,
  },
  absoluteFill: {
    position: 'absolute' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  animationOverlay: {
    position: 'absolute' as const,
    top: ANIMATION_OVERLAY_TOP_OFFSET,
    left: 0,
    right: 0,
    bottom: 0,
  },
}));
